import React from 'react';
import { Sparkles, Save, Info, AlertCircle } from 'lucide-react';

const MODEL_OPTIONS = [
    {
        value: 'quail_vf_l',
        label: 'Quail Voice Focus L',
        description: 'Best for single caller / near-field (phones, headsets). Recommended for voice AI.',
    },
    {
        value: 'quail_vf_s',
        label: 'Quail Voice Focus S',
        description: 'Smaller, faster Voice Focus variant with similar use case.',
    },
    {
        value: 'quail_l',
        label: 'Quail L',
        description: 'Better for speakerphone or multiple distant speakers.',
    },
];

const AiAcousticsTab = ({ agent, formData, setFormData, onSubmit }) => {
    const enabled = formData.ai_acoustics_enabled ?? false;
    const enhancementLevel = formData.ai_acoustics_enhancement_level ?? 0.8;
    const model = formData.ai_acoustics_model ?? 'quail_vf_l';
    const dtlnEnabled = formData.dtln_enabled ?? false;

    const handleToggle = (e) => {
        e.preventDefault();
        const next = !enabled;
        setFormData({
            ...formData,
            ai_acoustics_enabled: next,
            ...(next ? { dtln_enabled: false } : {}),
        });
    };

    const handleLevelChange = (e) => {
        setFormData({
            ...formData,
            ai_acoustics_enhancement_level: parseFloat(e.target.value),
        });
    };

    const handleModelChange = (e) => {
        setFormData({ ...formData, ai_acoustics_model: e.target.value });
    };

    return (
        <div
            className="flex-1 overflow-y-auto min-h-0 p-6"
            style={{ scrollBehavior: 'smooth', maxHeight: 'calc(100vh - 280px)' }}
        >
            <div className="max-w-2xl mx-auto space-y-6">
                <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                            <Sparkles className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-foreground">ai-coustics Speech Enhancement</h3>
                            <p className="text-sm text-muted-foreground">
                                Quail models tuned for voice AI — improves STT accuracy in noisy calls
                            </p>
                        </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg mb-4">
                        <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-blue-300 space-y-1">
                            <p className="font-medium text-blue-200">Self-hosted LiveKit</p>
                            <p>
                                Disabled by default. When enabled, this replaces DTLN and LiveKit BVC for that agent.
                            </p>
                        </div>
                    </div>

                    {dtlnEnabled && !enabled && (
                        <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-4">
                            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-300">
                                DTLN is enabled on this agent. Enabling ai-coustics will turn DTLN off — only one
                                noise processor runs per call.
                            </p>
                        </div>
                    )}

                    <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
                        <div>
                            <label className="text-sm font-medium text-foreground">
                                Enable ai-coustics Noise Cancellation
                            </label>
                            <p className="text-xs text-muted-foreground mt-1">
                                Real-time Quail speech enhancement on caller audio (off by default)
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleToggle}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                enabled ? 'bg-primary' : 'bg-muted'
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    enabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                        </button>
                    </div>

                    {enabled && (
                        <div className="mt-4 space-y-4">
                            <div className="p-4 bg-muted/20 rounded-lg border border-border/50 space-y-2">
                                <label className="text-sm font-medium text-foreground">Enhancement Model</label>
                                <select
                                    value={model}
                                    onChange={handleModelChange}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground"
                                >
                                    {MODEL_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-muted-foreground">
                                    {MODEL_OPTIONS.find((o) => o.value === model)?.description}
                                </p>
                            </div>

                            <div className="p-4 bg-muted/20 rounded-lg border border-border/50 space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="text-sm font-medium text-foreground">Enhancement Level</label>
                                    <span className="text-xs text-muted-foreground font-mono">
                                        {enhancementLevel.toFixed(2)}
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min="0.5"
                                    max="1"
                                    step="0.05"
                                    value={enhancementLevel}
                                    onChange={handleLevelChange}
                                    className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>0.5 — conservative</span>
                                    <span>0.8 — balanced (recommended)</span>
                                    <span>1.0 — aggressive</span>
                                </div>
                                <p className="text-xs text-muted-foreground pt-1">
                                    Higher levels suppress more background noise and interfering speech.{' '}
                                    <strong>0.8</strong> is the recommended balance for transcription accuracy.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {!enabled && (
                    <div className="bg-muted/20 rounded-lg border border-border/50 p-8 text-center">
                        <Sparkles className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                        <p className="text-sm text-muted-foreground">
                            Enable ai-coustics to configure model and enhancement level for this agent
                        </p>
                    </div>
                )}

                {agent?.id && (
                    <div className="flex justify-end pt-4 border-t border-border/50">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onSubmit) {
                                    const syntheticEvent = {
                                        preventDefault: () => {},
                                        target: e.target.closest('form') || e.target,
                                        currentTarget: e.target.closest('form') || e.target,
                                    };
                                    onSubmit(syntheticEvent);
                                }
                            }}
                            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30 font-medium"
                        >
                            <Save className="w-4 h-4" />
                            Save Changes
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AiAcousticsTab;
